<template>
	<div class="person-panel-filters">
		<div class="person-panel-filters__top">
			<app-resizable-dropdown
				position="left"
				resizable
				:initial-width="320"
				:initial-height="398"
				:min-width="320"
				:max-width="480"
				:min-height="398"
				:max-height="600"
				:close-by-click-outside="false"
				ref="$createEditContactModal"
				name="$createContactModal"
			>
				<template #activator>
					<app-tooltip position="bottom" text="Создать контакт">
						<template #activator>
							<app-icon
								class="person-panel-filters__btn"
								:style="{ marginRight: '4px' }"
								name="plus"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<create-edit-contact-dropdown
					type="create"
					@on-close="$createEditContactModal.close()"
					@exist-contact-string="showExistContacts"
				/>
			</app-resizable-dropdown>
			<app-input
				v-model="filters"
				:style="{ width: '100%' }"
				placeholder="Поиск по контактам"
				@input="updateFilters"
				@pressEnter="searchClients"
			/>
			<app-resizable-dropdown
				ref="$tagsDropdown"
				position="left"
				:close-by-click-outside="true"
				:max-width="640"
				:max-height="436"
				name="$mainPageContactsTags"
			>
				<template #activator>
					<app-tooltip text="Теги">
						<template #activator>
							<app-icon
								class="person-panel-filters__btn"
								:style="{ marginLeft: '4px' }"
								name="hash"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<app-tag-selector
					:model-value="selectedTags"
					:entity-type="TagEntityType.Person"
					mode="filter"
					allow-create
					@update:model-value="emit('update:selected-tags', $event)"
					@close="$tagsDropdown?.close()"
				/>
			</app-resizable-dropdown>
		</div>
		<div class="person-panel-filters__address">
			<inline-address-editor
				:id="'filter-main'"
				v-model="appliedAddress"
				:always-editing="true"
			/>
		</div>
		<div
			v-if="selectedTags.length"
			class="person-panel-filters__tags"
		>
			<div
				v-for="tag in selectedTags"
				class="person-panel-filters__tags-item"
				:key="tag.Id"
			>
				<div class="person-panel-filters__tags-item-name">
					{{ tag.Name }}
				</div>
				<app-icon
					class="person-panel-filters__tags-item-delete"
					name="close-bold"
					width="12px"
					color="#D2D2D2"
					hover-color="#4D4D4D"
					cursor="pointer"
					@click.stop="emit('drop-tag', tag)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import CreateEditContactDropdown from "@/components/main/clients-panel/dropdowns/CreateEditContactDropdown.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import InlineAddressEditor from "@/components/common/address-selector/InlineAddressEditor.vue";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

type Props = {
	modelValue: string,
	selectedTags: TagWithFrequency[],
}

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void,
	(e: 'update:selected-tags', value: TagWithFrequency[]): void,
	(e: 'drop-tag', tag: TagWithFrequency): void,
	(e: 'on-search', address?: RequestAddressContainer | null): void,
}>()

const props = defineProps<Props>()
const filters = ref(props.modelValue);

const $createEditContactModal = ref<typeof AppResizableDropdown | null>(null)
const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const appliedAddress = ref<RequestAddressContainer | null>(null)

const updateFilters = (newVal: string) => {
	emit('update:modelValue', newVal)
}

const searchClients = async () => {
	emit('on-search', appliedAddress.value)
}

const showExistContacts = (contacts: string) => {
	filters.value = contacts
	updateFilters(contacts)
}
</script>

<style scoped lang="scss">
.person-panel-filters {
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: 4px;
	width: 100%;
	background: #FFFFFF;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	border-bottom: 1px solid #F2F2F2;
	padding: 8px;

	&__top {
		display: flex;
		align-items: center;
		width: 100%;
	}

	&__address {
		width: 100%;
		box-sizing: border-box;
		padding-left: 22px;
	}

	&__search {
		width: 100%;
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px;
		width: 100%;
		box-sizing: border-box;
		padding-left: 22px;

		&-item {
			display: flex;
			align-items: center;
			text-transform: lowercase;

			&-name {
				color: #979797;
				font-size: 14px;
				line-height: 18px;
				margin-right: 2px;
			}
		}
	}

	&::v-deep(.q-field__control) {
		height: 32px;
	}

	&::v-deep(.q-field__append) {
		height: 32px;
	}
}
</style>
